import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box, useGetWarehouseBoxesQuery } from 'entities/Box';
import { SizeGroup } from 'entities/SizeCode';
import { getPlacedShapeIds } from '../../model/selectors/getPlacedShapeIds';
import { getSelectedFloorId } from '../../model/selectors/getSelectedFloorId';

export interface PickerBoxGroup {
  sizeCodeId: string;
  sizeGroup: SizeGroup;
  square: number;
  boxes: Box[];
}

export interface PickerBoxesApi {
  allUnplacedBoxes: Box[];
  groupsBySizeCode: PickerBoxGroup[];
  unplacedCountBySizeCode: Map<string, number>;
}

interface HookArgs {
  warehouseId: string | undefined;
}

export const usePickerBoxes = ({ warehouseId }: HookArgs): PickerBoxesApi => {
  const selectedFloorId = useAppSelector(getSelectedFloorId);
  // Subscribe only to the array of placed shape ids with shallow equality.
  // The picker is mounted at all times (right-rail palette), and the slice
  // dispatches selection / drag / transform actions on every mousemove. By
  // narrowing this hook's subscription we ensure those high-frequency
  // mutations don't invalidate the derived groups + counts and therefore
  // don't re-render `BoxesList` and `SizeCodeSelect` during marquee/drag.
  const placedShapeIds = useAppSelector(getPlacedShapeIds, shallowEqual);

  const { data } = useGetWarehouseBoxesQuery(
    { warehouseId, params: { floorId: selectedFloorId || undefined } },
    { skip: !warehouseId || !selectedFloorId },
  );

  return useMemo<PickerBoxesApi>(() => {
    const placedIds = new Set(placedShapeIds);
    const allUnplacedBoxes = (data ?? []).filter((box) => !placedIds.has(box.boxId));

    const groupsMap = new Map<string, PickerBoxGroup>();
    for (const box of allUnplacedBoxes) {
      const key = box.sizeCodeId;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          sizeCodeId: key,
          sizeGroup: box.sizeCode.sizeGroup,
          square: box.sizeCode.square,
          boxes: [],
        });
      }
      groupsMap.get(key)!.boxes.push(box);
    }

    const groupsBySizeCode = Array.from(groupsMap.values()).sort((a, b) => a.square - b.square);

    const unplacedCountBySizeCode = new Map<string, number>();
    for (const group of groupsBySizeCode) {
      unplacedCountBySizeCode.set(group.sizeCodeId, group.boxes.length);
    }

    return { allUnplacedBoxes, groupsBySizeCode, unplacedCountBySizeCode };
  }, [data, placedShapeIds]);
};
